import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Toast,
} from '@/components/ui';
import { formatRelative } from '@/lib/format';
import {
  ASTERISK_STATE_HELP as STATE_HELP,
  ASTERISK_STATE_LABELS as STATE_LABELS,
  ASTERISK_STATE_TONES as STATE_TONES,
} from '@/components/AsteriskState';

interface Server {
  id: string;
  name: string;
  code: string | null;
  base_url: string;
  enabled: boolean;
  mock_mode: boolean;
  sip_host: string | null;
  sip_port: number | null;
  remote_agent_ext: string | null;
  closer_did: string | null;
  closer_in_group: string | null;
  asterisk_state: string;
  asterisk_applied_at: string | null;
  asterisk_error: string | null;
  connection_state: 'UNKNOWN' | 'OK' | 'FAILED' | 'MOCK';
  connection_checked_at: string | null;
  connection_error: string | null;
  agents_ok: number | null;
  agents_total: number | null;
}

interface ConfigPreview {
  code: string;
  config: string;
  pjsip_path: string;
  extensions_path: string;
}

/**
 * Vicidial servers for one client, with their Asterisk side.
 *
 * Applying is a separate action from saving. The configuration is generated and
 * shown first, because a `pjsip.conf` written straight from a form is one typo
 * away from taking a trunk down — and the person who typed it should see what
 * it produced. Applying itself reloads rather than restarts Asterisk, so calls
 * already in progress are not affected.
 */
export function VicidialServers({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<ConfigPreview | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const servers = useQuery({
    queryKey: ['vicidial-integrations', tenantId],
    queryFn: () =>
      api.get<{ data: Server[] }>('/vicidial-integrations', {
        tenant_id: tenantId,
        page_size: 50,
      }),
  });

  const showConfig = useMutation({
    mutationFn: (id: string) =>
      api.get<ConfigPreview>(`/vicidial-integrations/${id}/asterisk-config`, {
        tenant_id: tenantId,
      }),
    onSuccess: (data) => setPreview(data),
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Could not generate the configuration'),
        tone: 'bad',
      }),
  });

  const apply = useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; detail: string; code: string }>(
        `/vicidial-integrations/${id}/asterisk-apply?tenant_id=${tenantId}`,
        {},
      ),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-integrations', tenantId] });
      setToast({
        message: result.ok
          ? `${result.code} is live. Calls in progress were not affected.`
          : result.detail,
        tone: result.ok ? 'good' : 'bad',
      });
    },
    onError: (err) =>
      setToast({
        message: errorMessage(err, 'Apply failed'),
        tone: 'bad',
      }),
  });

  const rows = servers.data?.data ?? [];

  return (
    <>
      <Card
        title="Vicidial servers"
        description="Each dialer this client runs, and its Asterisk trunk."
        padded={false}
      >
        {servers.isPending ? (
          <div className="space-y-2 p-4">
            <div className="skeleton h-12" />
            <div className="skeleton h-12" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No Vicidial servers yet"
            description="Add one per dialer. Ten bots on one server and ten on another is a normal setup."
          />
        ) : (
          <ul className="divide-y divide-line">
            {rows.map((server) => {
              const busy =
                (apply.isPending && apply.variables === server.id) ||
                (showConfig.isPending && showConfig.variables === server.id);
              // Apply is disabled without these, and a disabled button that
              // does nothing when pressed reads as broken software rather than
              // as a missing field. Name the field instead.
              const missing = [
                !server.code && 'a dialplan code',
                !server.sip_host && 'the Vicidial SIP host',
                !server.remote_agent_ext && 'the Remote Agent extension',
              ].filter(Boolean) as string[];
              const configurable = missing.length === 0;
              const blockedReason = configurable
                ? undefined
                : `Set ${missing.join(' and ')} on this server first — ` +
                  'edit it on the client’s Vicidial servers page.';

              return (
                <li key={server.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-ink-900 dark:text-ink-50">
                          {server.name}
                        </span>
                        {server.code && (
                          <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-600 dark:text-ink-300">
                            {server.code}
                          </code>
                        )}
                        {!server.enabled && <Badge tone="gray">Disabled</Badge>}
                        {server.mock_mode && <Badge tone="amber">Mock</Badge>}
                        {/* Whether the dialer answers, not whether somebody
                            ticked "enabled" — the same badge the client sees. */}
                        <Badge
                          tone={
                            server.connection_state === 'OK'
                              ? 'green'
                              : server.connection_state === 'FAILED'
                                ? 'red'
                                : 'gray'
                          }
                        >
                          {server.connection_state === 'OK'
                            ? 'Connected'
                            : server.connection_state === 'FAILED'
                              ? 'Not connected'
                              : server.connection_state === 'MOCK'
                                ? 'Not contacted'
                                : 'Never tested'}
                        </Badge>
                        {server.agents_total !== null && server.agents_total > 0 && (
                          <Badge
                            tone={
                              server.agents_ok === server.agents_total
                                ? 'green'
                                : server.agents_ok
                                  ? 'amber'
                                  : 'red'
                            }
                          >
                            {server.agents_ok ?? 0}/{server.agents_total} agents can log in
                          </Badge>
                        )}
                      </div>

                      <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-2xs text-ink-500 dark:text-ink-400">
                        <div>
                          <dt className="inline font-medium">SIP </dt>
                          <dd className="inline font-mono">
                            {server.sip_host
                              ? `${server.sip_host}:${server.sip_port ?? 5060}`
                              : 'not set'}
                          </dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Agent ext </dt>
                          <dd className="inline font-mono">{server.remote_agent_ext ?? '—'}</dd>
                        </div>
                        <div>
                          <dt className="inline font-medium">Closer DID </dt>
                          <dd className="inline font-mono">{server.closer_did ?? '—'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <Badge tone={STATE_TONES[server.asterisk_state] ?? 'gray'}>
                        {STATE_LABELS[server.asterisk_state] ?? server.asterisk_state}
                      </Badge>
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!configurable || busy}
                          title={blockedReason}
                          onClick={() => showConfig.mutate(server.id)}
                        >
                          View config
                        </Button>
                        <Button
                          size="sm"
                          disabled={!configurable}
                          title={blockedReason}
                          loading={apply.isPending && apply.variables === server.id}
                          onClick={() => apply.mutate(server.id)}
                        >
                          {server.asterisk_state === 'APPLIED' ? 'Re-apply' : 'Apply'}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <p className="mt-2 text-2xs text-ink-500 dark:text-ink-400">
                    {blockedReason ?? STATE_HELP[server.asterisk_state] ?? ''}
                    {server.asterisk_applied_at && server.asterisk_state === 'APPLIED' && (
                      <> Applied {formatRelative(server.asterisk_applied_at)}.</>
                    )}
                  </p>

                  {server.asterisk_error && (
                    <pre className="scroll-x mt-2 max-h-24 rounded-lg border border-red-200 bg-red-50 p-2 text-2xs text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                      {server.asterisk_error}
                    </pre>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Modal
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={`Asterisk configuration — ${preview?.code ?? ''}`}
        wide
        footer={
          <Button variant="secondary" onClick={() => setPreview(null)}>
            Close
          </Button>
        }
      >
        <p className="mb-3 text-sm text-ink-600 dark:text-ink-300">
          This is written to{' '}
          <code className="font-mono text-xs">{preview?.pjsip_path}</code> and{' '}
          <code className="font-mono text-xs">{preview?.extensions_path}</code>, then
          Asterisk is reloaded. A reload re-reads configuration without dropping
          calls that are already connected.
        </p>
        <pre className="scroll-x max-h-[55vh] overflow-y-auto rounded-lg border border-line bg-sunken p-3 text-2xs leading-relaxed text-ink-700 dark:text-ink-200">
          {preview?.config}
        </pre>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
